import type ControlBase from '../fabric/ControlBase';
import { type Constructor } from '../types';
import { fabric } from 'fabric';
import { hasPersistentControlBehaviors } from './guards';
import type Canvas from './Canvas';

interface PersistentControlTarget {
  target: fabric.Object;
  control: ControlBase;
}

// The half of persistent control logic that is added to the canvas
// object. This code handles finding persistent controls based on
// mouse events and delegating those events to the appropriate
// persistent control. For more on persistent controls, see
// ShapePersistentControlBehaviors.
// eslint-disable-next-line @typescript-eslint/no-redeclare
function CanvasPersistentControlBehaviors(Base: Constructor<Canvas>): any {
  return class extends Base {
    __targetPersistentControl: PersistentControlTarget | null;
    __previousTargetPersistentControl: PersistentControlTarget | null;

    findTarget(e: Event, skipGroup: boolean): fabric.Object | undefined {
      const pointer = this.getPointer(e, true);
      const isTouch = fabric.util.isTouchEvent(e);
      const control = this._findTargetPersistentControl(pointer, isTouch);

      this.__targetPersistentControl = control;

      if (!control) {
        return super.findTarget(e, skipGroup);
      }
    }

    _setCursorFromEvent(e: MouseEvent, target: fabric.Object): void {
      if (this.__targetPersistentControl) {
        const { control, target } = this.__targetPersistentControl;

        this.setCursor(control.cursorStyleHandler(e, control, target));
      } else {
        super._setCursorFromEvent(e, target);
      }
    }

    _handleEvent(
      e: MouseEvent,
      eventType: string,
      button: number,
      isClick: boolean
    ): void {
      if (
        this.__targetPersistentControl &&
        (button === undefined || button === 1) // left click
      ) {
        const { control, target } = this.__targetPersistentControl;

        // All our handlers need is the target, so ignore the rest of the transform properties
        const transform = { target } as fabric.Transform;

        if (eventType === 'down') {
          const pointer = this.getPointer(e, true);
          const mouseDownHandler = control.getMouseDownHandler(
            e,
            target,
            control
          );
          if (mouseDownHandler) {
            mouseDownHandler(e, transform, pointer.x, pointer.y);
          }
        } else if (eventType === 'up') {
          const pointer = this.getPointer(e, true);
          const mouseUpHandler = control.getMouseUpHandler(e, target, control);
          if (mouseUpHandler) {
            mouseUpHandler(e, transform, pointer.x, pointer.y);
          }
        }
      }

      if (eventType === 'move') {
        const leave: PersistentControlTarget | { control: null; target: null } =
          this.__previousTargetPersistentControl ?? {
            control: null,
            target: null,
          };
        const enter: PersistentControlTarget | { control: null; target: null } =
          this.__targetPersistentControl ?? {
            control: null,
            target: null,
          };

        const targetChanged =
          leave.control !== enter.control || leave.target !== enter.target;

        if (targetChanged) {
          if (this.__previousTargetPersistentControl) {
            const { control, target } = this.__previousTargetPersistentControl;
            if (control.onMouseOut) {
              control.onMouseOut(e, target);
            }
          }

          if (this.__targetPersistentControl) {
            const { control, target } = this.__targetPersistentControl;
            if (control.onMouseOver) {
              control.onMouseOver(e, target);
            }
          }
          this.__previousTargetPersistentControl =
            this.__targetPersistentControl;
        }

        if (this.__targetPersistentControl) {
          const { control, target } = this.__targetPersistentControl;
          if (control.onMouseMove) {
            control.onMouseMove(e, target);
          }
        }
      }
      super._handleEvent(e, eventType, button, isClick);
    }

    _findTargetPersistentControl(
      pointer: { x: number; y: number },
      isTouch: boolean
    ): PersistentControlTarget | null {
      const objects = this.renderedObjects;
      let i = objects.length;

      while (i--) {
        const obj = objects[i];

        if (hasPersistentControlBehaviors(obj)) {
          const control = obj.findTargetPersistentControl(pointer, isTouch);
          if (control)
            return {
              target: obj,
              control,
            };
        }
      }

      return null;
    }

    // Overrides the default drawControls method of fabric to also render the
    // persistent controls for each object.
    drawControls(ctx: CanvasRenderingContext2D): void {
      super.drawControls(ctx);

      const objects = this.renderedObjects;
      let i = objects.length;

      while (i--) {
        const object = objects[i];
        if (
          hasPersistentControlBehaviors(object) &&
          object.hasPersistentControls
        ) {
          object.renderPersistentControls(ctx);
        }
      }
    }
  };
}

export default CanvasPersistentControlBehaviors;
